/**
 * Scoped Storage Service for Epic Hyperspace Support
 *
 * PROBLEM:
 * In Epic Hyperspace, the embedded browser shares sessionStorage across all instances (tabs/frames).
 * This means if a user has two patient charts open, they share the same sessionStorage.
 * This causes critical state overwrites (e.g., Patient A's data showing in Patient B's tab).
 *
 * SOLUTION:
 * We must namespace all sessionStorage keys with a unique "Instance ID".
 * This Instance ID must be preserved in the URL (query string) so it survives reloads.
 *
 * STRATEGY:
 * 1. The backend provides a unique `launch_context` (UUID) upon successful auth.
 * 2. We use this `launch_context` as our `instanceId`.
 * 3. We persist this `instanceId` in the URL query parameter `instance`.
 * 4. All storage operations are prefixed with `${instanceId}_`.
 */
const INSTANCE_PARAM = 'instance';
/**
 * Get the current instance ID from the URL.
 * @returns The instance ID or null if not found.
 */
export const getInstanceId = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get(INSTANCE_PARAM) || params.get('launch_context');
};
/**
 * Generate a new random instance ID (UUID v4 style).
 * Used if no launch context is available (e.g. standalone launch without context).
 */
export const generateInstanceId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};
/**
 * Get the scoped key for a given key.
 * @param key - The original key (e.g., "patient_name")
 * @returns The scoped key (e.g., "abc-123_patient_name")
 */
const getScopedKey = (key) => {
    const instanceId = getInstanceId();
    if (!instanceId) {
        return key;
    }
    return `${instanceId}_${key}`;
};
/**
 * Scoped Storage API (drop-in replacement for sessionStorage)
 */
export const scopedStorage = {
    getItem: (key) => {
        return sessionStorage.getItem(getScopedKey(key));
    },
    setItem: (key, value) => {
        sessionStorage.setItem(getScopedKey(key), value);
    },
    removeItem: (key) => {
        sessionStorage.removeItem(getScopedKey(key));
    },
    /**
     * Clear all items for the current instance.
     * Does NOT clear items for other instances.
     */
    clear: () => {
        const instanceId = getInstanceId();
        if (!instanceId) {
            sessionStorage.clear();
            return;
        }
        const prefix = `${instanceId}_`;
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(prefix)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }
};
/**
 * Set the instance ID in the URL without reloading the page.
 * This ensures the ID is preserved across reloads.
 */
export const setInstanceIdInUrl = (instanceId) => {
    const url = new URL(window.location.href);
    url.searchParams.set(INSTANCE_PARAM, instanceId);
    // Also remove legacy launch_context if it exists (we normalized it to 'instance')
    // But we might keep it if we want to be safe. Let's keep it clean.
    // url.searchParams.delete('launch_context');
    window.history.replaceState({}, '', url.toString());
};
/**
 * Append the instance ID to a target URL.
 * Useful for constructing links that need to preserve the instance.
 */
export const appendInstanceId = (url) => {
    const instanceId = getInstanceId();
    if (!instanceId)
        return url;
    // Don't modify external URLs
    if (url.startsWith('http') || url.startsWith('//'))
        return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${INSTANCE_PARAM}=${instanceId}`;
};
